'use client'

import IconifyIcon from '@/components/wrappers/IconifyIcon'
import AnimatedCurrency from '@/app/(other)/auth/login/components/AnimatedCurrency'
import AnimatedNumber from '@/app/(other)/auth/login/components/AnimatedNumber'
import styles from '@/app/(other)/auth/login/login.module.css'

const CHART_POINTS = [
  { x: 0, y: 72 },
  { x: 48, y: 64 },
  { x: 96, y: 52 },
  { x: 144, y: 38 },
  { x: 192, y: 24 },
  { x: 240, y: 12 },
] as const

const BAR_HEIGHTS = [32, 40, 28, 48, 36, 44, 52] as const

const PNL_METRICS = [
  { label: 'Revenue', end: 128000, prefix: '$', compact: true, icon: 'solar:chart-2-bold-duotone', className: 'pnlMetricRevenue', delay: 0.4 },
  { label: 'Expenses', end: 42000, prefix: '$', compact: true, icon: 'solar:wallet-money-bold-duotone', className: 'pnlMetricExpenses', delay: 0.55 },
  { label: 'Profit', end: 18.4, suffix: '%', signed: true, decimals: 1, icon: 'solar:bill-list-bold-duotone', className: 'pnlMetricProfit', delay: 0.7 },
  { label: 'Margin', end: 32, suffix: '%', icon: 'solar:graph-up-bold-duotone', className: 'pnlMetricMargin', delay: 0.65 },
  { label: 'Net P&L', end: 86000, prefix: '$', compact: true, icon: 'solar:chart-square-bold-duotone', className: 'pnlMetricNetPnl', delay: 0.8 },
  { label: 'Growth', end: 12, suffix: '%', signed: true, icon: 'solar:course-up-bold-duotone', className: 'pnlMetricGrowth', delay: 0.5 },
] as const

const PNL_STATEMENT = [
  { label: 'Revenue', end: 128400, positive: true, delay: 0.25 },
  { label: 'COGS', end: -28200, positive: false, delay: 0.35 },
  { label: 'Expenses', end: -13900, positive: false, delay: 0.45 },
] as const

export default function AuthMarketingPane() {
  return (
    <div className={styles.shapePane}>
      <div className={styles.shapeDecor} aria-hidden="true">
        <div className={styles.pnlDecorLayer}>
          <div className={styles.pnlStatement}>
            <span className={styles.pnlStatementTitle}>P&amp;L Snapshot</span>
            {PNL_STATEMENT.map((row) => (
              <div key={row.label} className={styles.pnlStatementRow}>
                <span>{row.label}</span>
                <AnimatedCurrency
                  end={row.end}
                  delay={row.delay * 1000}
                  loop
                  className={row.positive ? styles.pnlPositive : styles.pnlNegative}
                />
              </div>
            ))}
            <div className={`${styles.pnlStatementRow} ${styles.pnlStatementTotal}`}>
              <span>Net profit</span>
              <AnimatedNumber
                end={86300}
                delay={550}
                prefix="+$"
                loop
                className={`${styles.pnlPositive} ${styles.pnlAnimatedNumber}`}
              />
            </div>
          </div>

          <div className={styles.pnlTrendBadge}>
            <IconifyIcon icon="solar:arrow-right-up-bold" width={16} height={16} />
            <span>
              <AnimatedNumber end={12.4} delay={400} decimals={1} signed suffix="%" loop className={styles.pnlAnimatedNumber} />
              {' '}vs last month
            </span>
          </div>

          <div className={styles.pnlBarChart}>
            <span className={styles.pnlBarChartLabel}>Monthly revenue</span>
            <svg viewBox="0 0 120 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              {BAR_HEIGHTS.map((h, i) => (
                <rect
                  key={i}
                  x={4 + i * 16}
                  y={48 - h}
                  width="10"
                  height={h}
                  rx="2"
                  className={styles.pnlBar}
                  style={{ animationDelay: `${0.4 + i * 0.12}s` }}
                />
              ))}
            </svg>
          </div>

          <div className={styles.pnlChartCard}>
            <span className={styles.pnlChartBadge}>P&amp;L Overview</span>
            <svg className={styles.pnlSvg} viewBox="0 0 240 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="pnlAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(52, 211, 153, 0.45)" />
                  <stop offset="100%" stopColor="rgba(52, 211, 153, 0)" />
                </linearGradient>
              </defs>
              <path
                className={styles.pnlArea}
                d="M0 72 L48 64 L96 52 L144 38 L192 24 L240 12 L240 100 L0 100 Z"
                fill="url(#pnlAreaGradient)"
              />
              <path
                className={styles.pnlLine}
                d="M0 72 L48 64 L96 52 L144 38 L192 24 L240 12"
                stroke="rgba(52, 211, 153, 0.95)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {CHART_POINTS.map((point, i) => (
                <g key={point.x}>
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="5"
                    className={styles.pnlChartDotRing}
                    style={{ animationDelay: `${0.55 + i * 0.18}s` }}
                  />
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="3"
                    className={styles.pnlChartDot}
                    style={{ animationDelay: `${0.6 + i * 0.18}s` }}
                  />
                </g>
              ))}
              <circle r="4" className={styles.pnlChartCursor} fill="#6ee7b7">
                <animateMotion dur="4s" repeatCount="indefinite" path="M0 72 L48 64 L96 52 L144 38 L192 24 L240 12" />
              </circle>
            </svg>
          </div>

          <div className={styles.pnlMetrics}>
            {PNL_METRICS.map((metric) => (
              <div key={metric.label} className={`${styles.pnlMetric} ${styles[metric.className]}`}>
                <IconifyIcon icon={metric.icon} width={20} height={20} />
                <div>
                  <span className={styles.pnlMetricLabel}>{metric.label}</span>
                  <AnimatedNumber
                    end={metric.end}
                    delay={metric.delay * 1000}
                    prefix={'prefix' in metric ? metric.prefix : ''}
                    suffix={'suffix' in metric ? metric.suffix : ''}
                    signed={'signed' in metric ? metric.signed : false}
                    decimals={'decimals' in metric ? metric.decimals : 0}
                    compact={'compact' in metric ? metric.compact : false}
                    loop
                    className={`${styles.pnlMetricValue} ${styles.pnlAnimatedNumber}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.shapeContent}>
        <div className={styles.heroBlock}>
          <p className={styles.heroEyebrow}>Profit &amp; loss, simplified</p>
          <h1 className={styles.heroText}>
            Track revenue, profit,<br />
            and margins in one<br />
            intelligent hub
          </h1>
          <p className={styles.heroSubText}>
            Inventory, sales, and analytics — built for teams who live and breathe P&amp;L.
          </p>
        </div>
      </div>
    </div>
  )
}
